import { Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type AudiCalculationModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/AudiCalculationModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import AudiCalculationModuleActions from './AudiCalculationModuleActions';
import AudiCalculationModuleInnerPage from './AudiCalculationModuleInnerPage';
import { useAudiCalculationSettingsDrawer } from './AudiCalculationSetting';
import { HeaderTabs } from './types';

export type AudiCalculationModulePageProps = {
    module: AudiCalculationModuleWithPermissionsSpecsFragment;
};

const AudiCalculationModulePage = ({ module }: AudiCalculationModulePageProps) => {
    const { t } = useTranslation(['audiCalculationModuleDetails']);

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const addAudiCalculationSettingsDrawer = useAudiCalculationSettingsDrawer(module.id, module.company);

    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('audiCalculationModuleDetails:descriptions.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('audiCalculationModuleDetails:descriptions.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    const availableTabs = useMemo(
        () => [
            {
                label: t('audiCalculationModuleDetails:tabs.mainDetails.title'),
                key: HeaderTabs.MainDetails,
                show: true,
            },
            {
                label: t('audiCalculationModuleDetails:tabs.authorization.title'),
                key: HeaderTabs.Authorization,
                show: true,
            },
        ],
        [t]
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={
                <AudiCalculationModuleActions
                    module={module}
                    openAudiCalculationSettingDrawer={addAudiCalculationSettingsDrawer.open}
                    permissions={permissions}
                />
            }
            header={{
                footer: (
                    <Tabs
                        activeKey={activeTab}
                        items={availableTabs.filter(tabs => tabs.show)}
                        onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                    />
                ),
            }}
            onBack={() => navigate(`/admin/system/modules`)}
            title={
                screens.md ? t('audiCalculationModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            {addAudiCalculationSettingsDrawer.render()}
            <AudiCalculationModuleInnerPage
                activeTab={activeTab}
                module={module}
                openAudiCalculationSettingDrawer={addAudiCalculationSettingsDrawer.open}
                permissions={permissions}
            />
        </ConsolePageWithHeader>
    );
};

export default AudiCalculationModulePage;
